/**
 * SCR-004 — Admin Dashboard.
 * Stat cards (branches/classes/students) + upcoming classes list.
 * Scoped by selectedBranchId from Zustand (admin only).
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';
import { Building2, GraduationCap, Users, AlertCircle, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAdminDashboard } from '@client/lib/queries/use-dashboard';
import { useBranchFilter } from '@client/store/branch-filter';

export const Route = createFileRoute('/admin/')({
  component: AdminDashboard,
});

function AdminDashboard() {
  const { session } = Route.useRouteContext() as {
    session?: { user?: { name?: string; role?: string } };
  };
  const role = session?.user?.role ?? '';
  const fullName = session?.user?.name ?? 'Bạn';
  const { selectedBranchId } = useBranchFilter();
  const branchId = role === 'admin' ? selectedBranchId : undefined;

  const { data, isLoading, isError, refetch } = useAdminDashboard(branchId);

  return (
    <div className="flex flex-col gap-6">
      {/* Greeting */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          Xin chào, {fullName}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Tổng quan hệ thống CLC Education LMS
        </p>
      </div>

      {/* Error */}
      {isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between gap-2 flex-wrap">
            <span>Không tải được dữ liệu.</span>
            <Button size="sm" variant="outline" onClick={() => void refetch()}>
              <RefreshCw className="h-3 w-3 mr-1" /> Thử lại
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Stat cards — 1 col mobile, 2 col sm, 3 col md */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
        {/* Branches — admin only */}
        {role === 'admin' && (
          <StatCard
            title="Cơ sở"
            icon={Building2}
            value={data?.counts.branches}
            isLoading={isLoading}
            href="/admin/branches"
            color="text-brand-green-600 dark:text-brand-green-400"
            bg="bg-card"
          />
        )}
        <StatCard
          title="Lớp học"
          icon={GraduationCap}
          value={data?.counts.classes}
          isLoading={isLoading}
          href="/admin/classes"
          color="text-brand-green-600 dark:text-brand-green-400"
          bg="bg-card"
        />
        <StatCard
          title="Học viên"
          icon={Users}
          value={data?.counts.students}
          isLoading={isLoading}
          href="/admin/students"
          color="text-brand-orange-600 dark:text-brand-orange-400"
          bg="bg-card"
        />
      </div>

      {/* Upcoming classes */}
      <div className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold text-foreground">
          Lớp sắp khai giảng (7 ngày tới)
        </h2>

        {isLoading && (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full rounded-lg" />
            ))}
          </div>
        )}

        {!isLoading && !isError && data?.upcomingClasses.length === 0 && (
          <div className="rounded-lg border border-border bg-muted/30 p-6 text-center text-muted-foreground text-sm">
            Không có lớp nào khai giảng trong 7 ngày tới.
          </div>
        )}

        {!isLoading && !isError && (data?.upcomingClasses ?? []).length > 0 && (
          <div className="flex flex-col gap-2">
            {data?.upcomingClasses.map((cls) => (
              <Link
                key={cls.id}
                to="/admin/classes/$classId"
                params={{ classId: cls.id }}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-lg border border-border bg-card p-4 hover:bg-muted/60 transition-colors"
              >
                <div className="flex flex-col gap-0.5 min-w-0">
                  <span className="font-mono text-sm font-semibold text-foreground truncate">
                    {cls.code}
                  </span>
                  <span className="text-xs text-muted-foreground truncate">
                    {cls.name} · {cls.branchName}
                  </span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Badge variant="secondary" className="text-xs">
                    {cls.currentEnrollment}/{cls.capacity} HV
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(cls.startDate)}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface StatCardProps {
  title: string;
  icon: React.ElementType;
  value?: number;
  isLoading: boolean;
  href: string;
  color: string;
  bg: string;
}

function StatCard({ title, icon: Icon, value, isLoading, href, color, bg }: StatCardProps) {
  return (
    <Link to={href}>
      <Card className={`hover:shadow-md transition-shadow cursor-pointer ${bg}`}>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
          <Icon className={`h-5 w-5 ${color}`} />
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-8 w-16" />
          ) : (
            <p className="text-3xl font-bold text-foreground">
              {(value ?? 0).toLocaleString('vi-VN')}
            </p>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

function formatDate(dateStr: string) {
  try {
    return format(new Date(dateStr), 'dd/MM/yyyy', { locale: vi });
  } catch {
    return dateStr;
  }
}
